// Global Instructions Rule Applied!
//
// A boolean the user flips in a grid row: a small centred antd Switch that
// saves on change (no confirm, no Save button). The switch shows the new
// value with its spinner while the save runs and goes back to the old value
// when the save fails; the caller owns the save, the toast and the refetch.
//
// `disabledReason` disables the switch and explains why in a Tooltip.
// Precedents: the Active switch of the fund Chart of Accounts tab, the
// Enabled switch of the Stage Actions panel.

import React, { useCallback, useEffect, useState } from 'react'
import { Switch, Tooltip } from 'antd'

interface GridSwitchCellProps {
  checked: boolean
  /** Saves the new value; resolves true when it is saved. */
  onSave: (next: boolean) => Promise<boolean>
  /** What the switch sets, for screen readers (the column header names it visually). */
  ariaLabel: string
  /** Set = the switch is disabled, with this as the tooltip. */
  disabledReason?: string | null
}

export default function GridSwitchCell({
  checked,
  onSave,
  ariaLabel,
  disabledReason
}: GridSwitchCellProps): React.ReactElement {
  const [saving, setSaving] = useState(false)
  // the value the user chose, shown until the row's own value catches up
  const [chosen, setChosen] = useState<boolean | null>(null)

  useEffect(() => {
    setChosen(null)
  }, [checked])

  const handleChange = useCallback(
    async (next: boolean, event: React.MouseEvent | React.KeyboardEvent) => {
      // the row may carry its own click handler
      event.stopPropagation()
      setChosen(next)
      setSaving(true)
      try {
        const saved = await onSave(next)
        if (!saved) setChosen(null)
      } catch {
        setChosen(null)
      } finally {
        setSaving(false)
      }
    },
    [onSave]
  )

  const control = (
    <Switch
      size='small'
      aria-label={ariaLabel}
      checked={chosen ?? checked}
      loading={saving}
      disabled={disabledReason != null}
      onChange={(next, event) => void handleChange(next, event)}
    />
  )

  return (
    <div className='flex justify-center'>
      {disabledReason ? (
        <Tooltip title={disabledReason}>
          <span>{control}</span>
        </Tooltip>
      ) : (
        control
      )}
    </div>
  )
}
